export type LoyaltySettings = {
  automaticEnabled: boolean;
  pauseInSafeMode: boolean;
  safeModeActive: boolean;
  followPoints: number;
  subscriptionPoints: number;
  resubscriptionPoints: number;
  giftPointsPerSubscription: number;
  cheerPointsPer100Bits: number;
  raidPoints: number;
  redemptionPoints: number;
  updatedBy: string | null;
  updatedAt: string | null;
  evidencePolicy: { supported: string[]; excluded: string[] };
};

export type LoyaltyViewer = {
  twitchUserId: string;
  login: string | null;
  displayName: string | null;
  balance: number;
  rank: number;
  firstSeenAt: string;
  lastSeenAt: string;
};

async function readJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      body && typeof body === 'object' &&
      typeof (body as Record<string, unknown>).message === 'string'
        ? String((body as Record<string, unknown>).message)
        : `HTTP ${response.status}`;
    throw new Error(message);
  }
  return body as T;
}

export async function loadLoyalty(): Promise<{
  settings: LoyaltySettings;
  viewers: LoyaltyViewer[];
  total: number;
}> {
  const [settings, leaderboard] = await Promise.all([
    fetch('/api/v1/loyalty/settings', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((response) => readJson<LoyaltySettings>(response)),
    fetch('/api/v1/loyalty/leaderboard?limit=50&offset=0', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((response) =>
      readJson<{ items: LoyaltyViewer[]; total: number }>(response)
    ),
  ]);
  return { settings, viewers: leaderboard.items, total: leaderboard.total };
}

export async function saveLoyaltySettings(
  settings: LoyaltySettings,
): Promise<LoyaltySettings> {
  return fetch('/api/v1/loyalty/settings', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({
      automaticEnabled: settings.automaticEnabled,
      pauseInSafeMode: settings.pauseInSafeMode,
      followPoints: settings.followPoints,
      subscriptionPoints: settings.subscriptionPoints,
      resubscriptionPoints: settings.resubscriptionPoints,
      giftPointsPerSubscription: settings.giftPointsPerSubscription,
      cheerPointsPer100Bits: settings.cheerPointsPer100Bits,
      raidPoints: settings.raidPoints,
      redemptionPoints: settings.redemptionPoints,
    }),
  }).then((response) => readJson<LoyaltySettings>(response));
}

export async function addLoyaltyAdjustment(input: {
  twitchUserId: string;
  amount: number;
  reason: string;
}): Promise<{ id: string }> {
  return fetch('/api/v1/foundation/loyalty-adjustments', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  }).then((response) => readJson<{ id: string }>(response));
}
